import { CompanyApi } from "src/api/generated/erp/common/companies/companyApi";
import { Company } from "src/api/generated/erp/db/types/tables/company";
import { FormCommonProps } from "../../../../common/forms/types";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { AsyncFetchRender } from "../../../../common/async/AsyncFetchRender.tsx";
import { enumOptions } from "src/components/common/forms/fields/selectionFieldUtils";
import { getCompanyCurrencyLabels } from "src/api/generated/erp/db/types/enums/companyCurrency";
import { DeepPartial, useForm } from "react-hook-form";

import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField";
import { spanGridColumns } from "src/components/common/forms/styles";
import {
	nonNegativeFloatRule,
	nonNegativeIntegerRule,
	requireRule,
} from "src/components/common/forms/validation";
import i18n from "i18next";

export interface CompanyFormProps extends FormCommonProps<number> {
	companyId?: number;
}

export const CompanyForm = ({ companyId, ...other }: CompanyFormProps) => {
	const fetchDefaultValues = async () => {
		return companyId !== undefined
			? CompanyApi.getSingle({ companyId })
			: NEW_RECORD_DEFAULT_VALUES;
	};

	return (
		<AsyncFetchRender
			fetch={fetchDefaultValues}
			content={(defaultValues) => {
				return <CompanyFormContent defaultValues={defaultValues} {...other} />;
			}}
		/>
	);
};

interface CompanyFormContentProps extends FormCommonProps<number> {
	defaultValues: DeepPartial<Company>;
}

const CompanyFormContent = ({ defaultValues, onFormEdited, ...other }: CompanyFormContentProps) => {
	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch,
	} = useForm<Company>({
		mode: "all",
		defaultValues: defaultValues,
	});

	if (onFormEdited) watch(onFormEdited);

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={submitForm}
			columns={2}
			{...other}
		>
			<FormTextField
				control={control}
				name={"companyName"}
				rules={requireRule()}
				label={i18n.t("company")}
				autoFocus
			/>
			<FormTextField
				control={control}
				name={"businessId"}
				label={i18n.t("business_id")}
				rules={requireRule()}
			/>
			<FormTextField
				control={control}
				name={"invoiceAddress"}
				label={i18n.t("invoice_address")}
				multiline={true}
				rules={requireRule()}
				sx={spanGridColumns}
			/>
			<FormTextField
				control={control}
				name={"einvoiceId"}
				label={i18n.t("e_invoice_id")}
				rules={requireRule()}
			/>
			<FormTextField
				control={control}
				name={"einvoiceOperator"}
				label={i18n.t("e_invoice_operator")}
				rules={requireRule()}
			/>

			<FormTextField
				control={control}
				name={"printoutText_1"}
				label={i18n.t("additional_info", { number: "1" })}
				multiline={true}
				sx={spanGridColumns}
			/>

			<FormTextField
				control={control}
				name={"printoutText_2"}
				label={i18n.t("additional_info", { number: "2" })}
				multiline={true}
				sx={spanGridColumns}
			/>

			<FormTextField
				control={control}
				name={"printoutText_3"}
				label={i18n.t("additional_info", { number: "3" })}
				multiline={true}
				sx={spanGridColumns}
			/>

			<FormTextField
				control={control}
				name={"printoutText_4"}
				label={i18n.t("additional_info", { number: "4" })}
				multiline={true}
				sx={spanGridColumns}
			/>
			<FormSelectField
				control={control}
				name={"currency"}
				label={i18n.t("currency")}
				disableClearable={true}
				rules={requireRule()}
				options={enumOptions(getCompanyCurrencyLabels())}
			/>
			<FormTextField
				control={control}
				name={"invoicePenaltyInterest"}
				label={i18n.t("invoice_penalty_interest_%")}
				rules={{
					...requireRule(),
					...nonNegativeFloatRule(),
				}}
				type={"number"}
			/>
			<FormTextField
				control={control}
				name={"invoiceRemindDays"}
				label={i18n.t("invoice_remind_days")}
				type={"number"}
				rules={{
					...requireRule(),
					...nonNegativeIntegerRule(),
				}}
			/>
			<FormTextField
				control={control}
				name={"bankAccountNumber"}
				label={i18n.t("bank_account_number")}
				rules={requireRule()}
			/>
		</AavoForm>
	);
};

const NEW_RECORD_DEFAULT_VALUES: DeepPartial<Company> = {
	invoicePenaltyInterest: 5,
	invoiceRemindDays: 7,
	currency: "EUR",
};

const submitForm = async (company: Company): Promise<number> => {
	const result = await CompanyApi.save({ company: company });
	return result.companyId;
};
